// ChatBox.tsx
import { use, useEffect, useRef, useState } from "react";
import { useSSEChat } from "@/utils/sse";
import AppButton from "@/components/structure/AppButton";

import Markdown from 'react-markdown'
import { ReviewMessage, TaskResponse } from "@/api/types";
import { apiClient } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface ChatBoxProps {
  taskResponse: TaskResponse;
}

export default function ChatBox({
  taskResponse
}: ChatBoxProps) {
  const { user, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState<ReviewMessage[]>(taskResponse?.reviewMessages || []);
  const [input, setInput] = useState("");

  const inputBox = useRef<HTMLTextAreaElement>(null);

  const topContainer = useRef<HTMLDivElement>(null);

  const refreshTaskResponse = async () => {
    // Placeholder for any action needed after response is complete
    apiClient.get<TaskResponse>(`/task-responses/${taskResponse.id}/`).then((updated: TaskResponse) => {
      console.log("Updated Task Response:", updated);
      setMessages(updated.reviewMessages || []);
    }).catch(err => {
      console.error("Failed to refresh task response:", err);
    });
  }

  const { text, sendMessage, status, abort } = useSSEChat(
    `/task-responses/${taskResponse.id}/llm-review/`,
    () => {
      refreshTaskResponse();
      // focus the input box
      if (inputBox.current) {
        inputBox.current.focus();
      }
    }
  );

  const getWrappedLines = (textarea: HTMLTextAreaElement) => {
    const style = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight);

    if (isNaN(lineHeight)) {
        // fallback if line-height is "normal"
        const fontSize = parseFloat(style.fontSize);
        // approximate normal line height as 1.2 × font-size
        return Math.round(textarea.scrollHeight / (fontSize * 1.2));
    }

    return Math.round(textarea.scrollHeight / lineHeight);
  }

  const resizeInputBox = () => {
    // set the height of the textarea based on content
    if (inputBox.current) {
      const lines = getWrappedLines(inputBox.current);
      const maxLines = 12;
      inputBox.current.style.height = 'auto'; // reset height
      inputBox.current.style.height = `${Math.min(lines, maxLines) * 1.2}em`; // set new height
    }
  }

  useEffect(() => {
    resizeInputBox();
  }, [input]);

  useEffect(() => {
    setMessages(taskResponse?.reviewMessages || []);
  }, [taskResponse, isAuthenticated]);

  const handleSendMessage = async (message: string) => {
    // optimistically add the message to the chat
    setMessages((prev) => [...prev, {
      id: Date.now().toString(), // temporary ID
      author: user?.username || "You",
      createdAt: undefined,
      content: message,
      isSystem: false,
      taskResponse: taskResponse.id,
    } as ReviewMessage]);

    await sendMessage(message);
    setInput("");
  }

  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clean up any ongoing SSE connections
      if (status !== "idle" && status !== "closed") {
        // The useSSEChat hook should handle cleanup automatically
        console.log("Page refreshing during active chat session");
        setInput("");
        abort();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [status]);

  // while a response is being received, keep the parent of topContainer scrolled to the bottom
  useEffect(() => {
    if (status == "connecting" || status == "open" || status == "closed") {
      const interval = setInterval(() => {
        if (topContainer.current) {
          const parent = topContainer.current.parentElement;
          if (parent) {
            // parent.scrollTop = parent.scrollHeight;
            // smooth scroll to bottom
            parent.scrollTo({
              top: parent.scrollHeight,
              behavior: 'smooth',
            });
          }
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [status]);

  return (
    <div className="w-full" ref={topContainer}>
      {/* Conversation to this point, included in the review_messages field in taskResponse */ }
      <div>
        <div className="p-2 my-2 flex flex-col space-y-2 w-full">
          { /* Shows the history of messages for this review */ }
          {messages.map((msg) => (
            <div key={msg.id} className={`mb-3 ${!msg.isSystem ? 'bg-gray-100 self-end p-3 rounded-xl w-3/4' : 'bg-gray-600 text-white self-start p-3 rounded-xl w-3/4'}`}>
              <div className={`text-sm text-gray-600 mb-1 ${msg.isSystem ? 'text-white' : ''}`}>
                <strong>{ msg.isSystem ? "LLM" : msg.author}</strong> <span className="text-xs italic">{msg.createdAt && `(${new Date(msg.createdAt).toLocaleString()})`}</span>
              </div>
              <div className="preflight chat-content">
                <Markdown>{msg.content}</Markdown>
              </div>
            </div>
          ))}

          { /* Shows the streaming response from the LLM as it's being generated */ }
          {(status == "connecting" || status == "open") && (
            <div key="incoming-system-msg" className={`mb-3 bg-gray-600 text-white self-start p-3 rounded-xl w-3/4`}>
              <div className={`text-sm mb-1 text-white`}>
                <strong>LLM</strong> <span className="text-xs italic">generating...</span>
              </div>
              {
                (status == "open" && text) ? (
                  <div className="preflight chat-content">
                    <Markdown>{text}</Markdown>
                  </div>
                ) : (
                  <div>
                    <FontAwesomeIcon icon="spinner" spin />
                  </div>
                )
              }
            </div>
          )}
        </div>

        {/* <div>
          Status: {status}
        </div> */}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage(input);
          }}
          className="flex items-end mb-6 w-full border border-gray-300 rounded-xl p-2"
        >
          <textarea
            ref={inputBox}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              // make Enter send the message, unless Shift is held in which case insert a newline
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (input && input.trim() != "") {
                  handleSendMessage(input);
                }
              }
            }}
            className="w-full p-2 pr-3 focus:outline-none"
            placeholder="Type something..."
          />
          <AppButton
            type="submit"
            variant="primary"
            className="ml-2 rounded-xl"
            forwardArrow={true}
            disabled={!input || (status != "idle" && status != "closed")}
          >
            {status == "idle" || status == "closed" ? "Send" : <>
              <FontAwesomeIcon icon="spinner" spin className="mr-2" /> {status}
            </>}
          </AppButton>
        </form>
      </div>
    </div>
  );
}
